/**
 * Client-side passphrase and password generation.
 *
 * "Layout-safe" output types identically on German (QWERTZ) and US (QWERTY)
 * keyboards, e.g. when typing into a remote console that assumes the other
 * layout: no y/z (swapped), no umlauts, and only symbols that sit on the same
 * key in both layouts.
 */

/** Uniform random integer in [0, n), via rejection sampling (no modulo bias). */
export const randomInt = (n: number): number => {
  if (!Number.isInteger(n) || n <= 0 || n > 2 ** 32) {
    throw new RangeError(`randomInt: invalid range ${n}`);
  }
  const limit = 2 ** 32 - (2 ** 32 % n); // largest multiple of n that fits
  const buf = new Uint32Array(1);
  for (;;) {
    crypto.getRandomValues(buf);
    if (buf[0] < limit) return buf[0] % n;
  }
};

const pick = <T,>(items: readonly T[]): T => items[randomInt(items.length)];

const isLayoutSafeWord = (word: string) => !/[yz]/i.test(word);

// ---------- Passphrases ----------

export interface PassphraseOptions {
  words: number
  separator: string
  capitalize: boolean
  addDigit: boolean
  layoutSafe: boolean
}

/** Separators offered in the UI; `layoutSafe` ones type the same on QWERTZ and QWERTY. */
export const SEPARATORS = [
  { value: '.', label: 'Punkt  .', layoutSafe: true },
  { value: ' ', label: 'Leerzeichen', layoutSafe: true },
  { value: '-', label: 'Bindestrich  -', layoutSafe: false },
  { value: '_', label: 'Unterstrich  _', layoutSafe: false },
] as const;

export const passphraseWordlist = (wordlist: readonly string[], layoutSafe: boolean) =>
  layoutSafe ? wordlist.filter(isLayoutSafeWord) : wordlist;

export const generatePassphrase = (wordlist: readonly string[], opts: PassphraseOptions): string => {
  const list = passphraseWordlist(wordlist, opts.layoutSafe);
  const words = Array.from({ length: opts.words }, () => {
    const word = pick(list);
    return opts.capitalize ? word[0].toUpperCase() + word.slice(1) : word;
  });
  if (opts.addDigit) {
    const i = randomInt(words.length);
    words[i] += String(randomInt(10));
  }
  return words.join(opts.separator);
};

/** Entropy in bits. Capitalization and separator are fixed, so they add nothing. */
export const passphraseEntropy = (wordlistSize: number, opts: PassphraseOptions): number =>
  opts.words * Math.log2(wordlistSize) + (opts.addDigit ? Math.log2(10 * opts.words) : 0);

// ---------- Passwords ----------

export interface PasswordOptions {
  length: number
  upper: boolean
  lower: boolean
  digits: boolean
  symbols: boolean
  avoidAmbiguous: boolean
  layoutSafe: boolean
}

const SYMBOLS_ALL = '!#$%&()*+,-./:;<=>?@[]^_{}~';
// Same key on QWERTZ and QWERTY: Shift+1, Shift+4, Shift+5, and the period key
const SYMBOLS_LAYOUT_SAFE = '!$%.';
const AMBIGUOUS = /[Il1O0o]/g;

export const passwordCharsets = (opts: PasswordOptions): string[] => {
  const strip = (chars: string) => {
    let result = chars;
    if (opts.layoutSafe) result = result.replace(/[yzYZ]/g, '');
    if (opts.avoidAmbiguous) result = result.replace(AMBIGUOUS, '');
    return result;
  };
  const sets: string[] = [];
  if (opts.upper) sets.push(strip('ABCDEFGHIJKLMNOPQRSTUVWXYZ'));
  if (opts.lower) sets.push(strip('abcdefghijklmnopqrstuvwxyz'));
  if (opts.digits) sets.push(strip('0123456789'));
  if (opts.symbols) sets.push(opts.layoutSafe ? SYMBOLS_LAYOUT_SAFE : SYMBOLS_ALL);
  return sets;
};

/**
 * Every character is drawn uniformly from the union of the selected sets.
 * Results missing a selected set are thrown away and redrawn, which keeps the
 * output uniform over all passwords that satisfy "at least one of each".
 */
export const generatePassword = (opts: PasswordOptions): string => {
  const sets = passwordCharsets(opts);
  if (sets.length === 0 || opts.length < sets.length) return '';
  const alphabet = sets.join('');
  for (;;) {
    const password = Array.from({ length: opts.length }, () => alphabet[randomInt(alphabet.length)]).join('');
    if (sets.every(set => password.split('').some(c => set.includes(c)))) return password;
  }
};

/** Upper bound in bits; the "one of each set" rule removes a negligible share. */
export const passwordEntropy = (opts: PasswordOptions): number => {
  const size = passwordCharsets(opts).join('').length;
  return size > 0 ? opts.length * Math.log2(size) : 0;
};
